import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { ApiQuerySuccessPromise } from '@/types';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthActions } from '@/store/auth';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { getResponseJwtHeader } from '@/utils/web';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { reservationKeys } from './keys';

export const fetchReservationCountOfMatch = async (custId: number, matchId: number): Promise<ApiQuerySuccessPromise<number>> => {
	const authToken = getAuthToken();
	const { apiDomain } = useSiteStore.getState();
	const response = await fetchComposer.postWithBearer(
		cloudUrlParser(`${apiDomain.ctcdDomain}/api/Reservation/GetMiniReservationTickets`),
		{
			body: JSON.stringify({
				custId,
				matchId
			})
		}
	)(authToken);
	
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`, { cause: response });
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useCheckReservationCount = (custId: number, matchId: number) => {
	const { data, isLoading, isError, error } = useQuery({
		queryKey: reservationKeys.count(custId, matchId),
		queryFn: () => fetchReservationCountOfMatch(custId, matchId),
		staleTime: 0,
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: custId !== 0,
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		data?.jwtToken && updateAuthToken(data.jwtToken);
	}, [data?.jwtToken]);

	if (isError) {
		console.error(error);
	}

	return {
		data: data?.data.Data,
		isLoading,
		isError,
		error,
	};
};